import { Link } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";
import { useCart } from "@/context/CartContext";

const GROUP_ACCENTS = {
  "Signage & Boards": "from-orange-500 to-red-500",
  "Large Format": "from-purple-500 to-pink-500",
  "Corporate Print": "from-blue-600 to-slate-800",
  "Marketing & Promotion": "from-emerald-500 to-teal-500",
  "Personalised & Gifting": "from-amber-500 to-orange-600",
  "Apparel & Accessories": "from-indigo-500 to-purple-700",
};

export default function Cart() {
  usePageTitle("Cart");
  const { cart, removeItem, loading } = useCart();

  if (loading && cart.items.length === 0) {
    return <div className="max-w-6xl mx-auto px-4 py-24 text-center text-zinc-500">Loading cart…</div>;
  }

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="w-24 h-24 mx-auto rounded-full bg-orange-50 flex items-center justify-center">
          <ShoppingBag size={40} className="text-[#FF6B00]" />
        </div>
        <h1 className="mt-6 font-display text-4xl font-black tracking-tighter">Your cart is empty</h1>
        <p className="mt-3 text-zinc-600">Browse our print range and add your favourites.</p>
        <Link to="/products" className="mt-8 inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3 rounded-full hover:bg-[#e65a00]" data-testid="browse-products-btn">
          Browse Products <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      <div className="mb-8">
        <div className="overline text-[#FF6B00]">Checkout · Step 1 of 3</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter">Shopping Cart</h1>
        <p className="mt-2 text-zinc-500">{cart.count} {cart.count === 1 ? "item" : "items"} in your cart</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Line items */}
        <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
          {cart.items.map((it) => {
            const accent = GROUP_ACCENTS[it.group] || "from-zinc-500 to-zinc-700";
            const lineId = it.line_id || it.product_id;
            return (
              <div key={lineId} className="flex gap-4 p-4 border-2 border-zinc-200 rounded-2xl bg-white" data-testid={`cart-item-${it.product_id}`}>
                <div className={`w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden ${it.image_url ? "" : `bg-gradient-to-br ${accent}`} flex items-center justify-center`}>
                  {it.image_url ? (
                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="text-white font-bold text-[10px] text-center px-2 leading-tight">{it.name}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${it.product_id}`} className="font-bold text-lg hover:text-[#FF6B00] block truncate">
                    {it.name}
                  </Link>
                  <div className="text-xs text-zinc-500">{it.category}</div>
                  {(it.options_display || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`cart-opts-${lineId}`}>
                      {it.options_display.map((od, i) => (
                        <span key={i} className="text-[10px] bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded">
                          <b>{od.label}:</b> {od.value_label}
                        </span>
                      ))}
                    </div>
                  )}
                  {it.design_choice && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{
                      background: it.design_choice === "upload_now" ? "#dcfce7" : it.design_choice === "upload_later" ? "#fef3c7" : "#e0f2fe",
                      color: it.design_choice === "upload_now" ? "#166534" : it.design_choice === "upload_later" ? "#92400e" : "#075985",
                    }} data-testid={`cart-design-choice-${lineId}`}>
                      {it.design_choice === "upload_now" && `📎 ${(it.design_files || []).length} file${(it.design_files || []).length === 1 ? "" : "s"} uploaded`}
                      {it.design_choice === "upload_later" && "⏱ Upload later"}
                      {it.design_choice === "design_for_me" && "🎨 Design for me"}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-3">
                    <div
                      className="inline-flex items-center border-2 border-zinc-200 rounded-full px-3 py-1 bg-zinc-50 text-xs font-bold text-zinc-700"
                      data-testid={`cart-qty-${lineId}`}
                    >
                      Qty: <span className="ml-1 font-mono">{it.quantity}</span>
                    </div>
                    <button
                      onClick={() => removeItem(lineId)}
                      className="text-xs text-zinc-500 hover:text-red-600 inline-flex items-center gap-1"
                      data-testid={`remove-${lineId}`}
                    ><Trash2 size={12} /> Remove</button>
                  </div>
                  <div className="mt-1 text-[10px] text-zinc-500">To change the quantity, remove this item and re-add it from the product page.</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-black text-[#FF6B00] text-lg">₹{Math.round(it.line_total).toLocaleString("en-IN")}</div>
                  <div className="text-[10px] text-zinc-500">₹{it.unit_price} / unit</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="p-6 border-2 border-zinc-200 rounded-2xl bg-white" data-testid="cart-summary">
            <div className="font-display text-2xl font-black tracking-tighter mb-4">Order Summary</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-600">Subtotal</span><span className="font-mono font-bold" data-testid="cart-subtotal">₹{Math.round(cart.subtotal).toLocaleString("en-IN")}</span></div>
              <div className="border-t border-zinc-200 pt-3 mt-3 flex justify-between text-lg">
                <span className="font-bold">Cart Total</span>
                <span className="font-mono font-black text-[#FF6B00]" data-testid="cart-total">₹{Math.round(cart.subtotal).toLocaleString("en-IN")}</span>
              </div>
              <div className="text-[10px] text-emerald-700 font-bold pt-1">All prices incl. of GST</div>
              <div className="text-[10px] text-zinc-500 pt-1">Shipping, coupons and gift cards are calculated at checkout.</div>
            </div>
            <Link to="/checkout" className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-bold px-6 py-3 rounded-full hover:bg-[#e65a00]" data-testid="proceed-to-checkout">
              Proceed to Checkout <ArrowRight size={14} />
            </Link>
            <Link to="/products" className="mt-3 w-full inline-flex items-center justify-center text-sm text-zinc-500 hover:text-[#FF6B00]">
              ← Continue Shopping
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
